import React, { useEffect, useRef, useState } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/compat/router';
import { MediaQueryProvider } from 'react-media-query-hoc';
import { Breadcrumbs } from '@domain-group/fe-co-header';
import { Paginator } from '@domain-group/fe-blueprint-ui/pagination';
import SearchResultsHead from '../head/search-result-head';
import SearchResultsFooterWithFragment from '../footer/search-results-footer-with-fragment';
import { UserDetailsRedacted } from '../../@types';
import { mapSearchResultsBreadcrumbs } from '../header/utils';
import {
  useRouteChangeLoading,
  useSearchResults,
  useSearchState,
  useTrackSearchResultsView,
} from './hooks';
import { Option } from '../search-autocomplete/types';
import {
  trackOpenFilters,
  trackTapSearch,
  trackSelectSortOrder,
  trackTapAgentCard,
  trackTapAgencyCard,
} from '../../tracking/search-results';
import {
  AppraisalCtaCard,
  Loader,
  LoadingOverlay,
  RmaDisclaimer,
  SearchResultCards,
  SearchStickyBar,
  SortBySelect,
} from './components';
import {
  buildUrl,
  buildPaginatorUrl,
  formatResultsHeading,
  isPropertyManagerFilter,
} from './utils';
import buildSearchResultsBaseProperties from './utils/build-search-results-base-properties';
import {
  SALES_AGENT,
  DEFAULT_SORT_LABEL,
  DEFAULT_SORT_VALUE,
  SORT_OPTIONS_AGENCIES_2026,
  SORT_OPTIONS_AGENTS_2026,
  AGENCY_VIEW,
} from './components/search-filter-bar/enums';
import * as styles from './search-results-2026.style';

const { publicRuntimeConfig } = getConfig();

/* eslint-disable react/no-unused-prop-types */
export interface SearchResults2026Props {
  mixpanelHasInitialized?: boolean;
  isEmbeddedApp?: boolean;
  skipLinkContentId: string;
  user?: UserDetailsRedacted;
  sortBy?: string;
  keywordName?: string;
  agentFilter?: string;
  page: string;
  isAgency: boolean;
  suburbs: string;
}
/* eslint-enable react/no-unused-prop-types */

const SearchResults2026 = ({
  mixpanelHasInitialized = false,
  isEmbeddedApp = false,
  skipLinkContentId,
  user,
  page: initialPage,
  keywordName: initialKeyword,
  agentFilter: initialAgentFilter,
  sortBy: initialSortBy,
  isAgency: initialIsAgency,
  suburbs: initialSuburbs,
}: SearchResults2026Props): JSX.Element => {
  const router = useRouter();
  const routeLoading = useRouteChangeLoading(router);

  const state = useSearchState({
    isAgency: initialIsAgency,
    suburbs: initialSuburbs,
    page: initialPage,
    keywordName: initialKeyword,
    agentFilter: initialAgentFilter,
    sortBy: initialSortBy,
  });

  const { data, loading, error, actualLocationProfiles, search, agencyIds } =
    useSearchResults(state);

  useTrackSearchResultsView({
    data,
    state,
    path: router?.asPath,
    loading,
    mixpanelHasInitialized,
  });

  const [isPending, setIsPending] = useState(false);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout>>();
  const buildStateKey = (searchState: typeof state): string =>
    searchState
      ? [
          searchState.isAgency,
          searchState.page,
          searchState.sortBy,
          searchState.agentFilter,
          searchState.keywordName,
          searchState.suburbs,
        ].join('|')
      : '';
  const stateKey = buildStateKey(state);
  useEffect(() => {
    setIsPending(false);
  }, [stateKey]);

  useEffect(() => {
    if (!routeLoading) {
      setIsPending(false);
    }
  }, [routeLoading]);

  const hasNoSuburbs = !loading && (!state || !actualLocationProfiles.length);

  // Can't redirect during getServerSideProps, so it happens once on the client
  useEffect(() => {
    if (hasNoSuburbs) {
      router?.push?.('/real-estate-agents');
    }
  }, [hasNoSuburbs, router]);

  if (error) {
    throw new Error(`Something went wrong, ${error}`);
  }

  if (!data || !state || !actualLocationProfiles.length) {
    return <Loader />;
  }

  const { isAgency, page } = state;

  const breadcrumbs = mapSearchResultsBreadcrumbs({
    isAgent: !isAgency,
    suburbs: actualLocationProfiles,
  });

  const changePage = (filters: Partial<typeof state>): void => {
    // Any filter change resets to page 1 unless the caller is explicitly
    // navigating pages (handlePageChange passes `page` itself).
    const newState = { ...state, ...filters, page: filters.page ?? 1 };
    const { to, as } = buildUrl(newState);
    const keepsCurrentSuburbs = typeof filters.suburbs === 'undefined';
    if (buildStateKey(newState) !== stateKey) {
      setIsPending(true);
    }

    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() =>
      window?.scrollTo({ top: 0, behavior: 'smooth' }),
    );
    router?.push(to, as, { shallow: keepsCurrentSuburbs });
  };

  const handlePageChange = (
    event: React.SyntheticEvent,
    newPage: number,
  ): void => {
    event.preventDefault();

    changePage({ page: newPage });
  };

  const setSortBy = (sortBy: string): void => {
    const sortOptions = isAgency
      ? SORT_OPTIONS_AGENCIES_2026
      : SORT_OPTIONS_AGENTS_2026;
    const sortOption = sortOptions.find((option) => option.value === sortBy);

    trackSelectSortOrder({
      sortOrderSelected: sortOption?.label ?? DEFAULT_SORT_LABEL,
      baseProperties: buildSearchResultsBaseProperties(data, state),
    });

    changePage({ sortBy: sortOption?.value ?? DEFAULT_SORT_VALUE });
  };

  const initialSelectedOption: Option[] = actualLocationProfiles.map(
    (profile) => ({
      category: 'Suburb' as Option['category'],
      label: `${profile?.suburbName}, ${profile?.state} ${profile?.postcode}`,
      value: profile?.urlSlug ?? '',
      url: profile?.urlSlug ?? '',
      redirectToURL: false,
    }),
  );

  const selectedSuburbsKey = initialSelectedOption
    .map(({ value }) => value)
    .join(',');

  const selectSuburbs = (options: Option[]): void => {
    changePage({ suburbs: options.map(({ value }) => value) });
  };

  const selectAgentType = (agentFilter: string): void => {
    changePage({ agentFilter });
  };

  const isLoading = !!(loading || routeLoading || isPending);

  const baseProperties = buildSearchResultsBaseProperties(data, state);

  const handleOpenFilters = (): void => {
    trackOpenFilters({ baseProperties });
  };

  const handleAgentTypeSearchSubmit = (agentFilter: string): void => {
    trackTapSearch({
      interactionCTA: isPropertyManagerFilter(agentFilter)
        ? 'Lease a property'
        : 'Sell a home',
      baseProperties,
    });
  };

  const selectSearchType = (view: string): void => {
    changePage({
      isAgency: view === AGENCY_VIEW,
      // Switching view resets the filters that do not apply across both
      page: 1,
      agentFilter: SALES_AGENT,
      keywordName: '',
      sortBy: DEFAULT_SORT_VALUE,
    });
  };

  return (
    <MediaQueryProvider values={{ width: 300, type: 'screen' }}>
      <div css={styles.page}>
        <SearchResultsHead
          isAgency={isAgency}
          total={search?.total}
          totalPages={search?.totalPages}
          pageNumber={page}
          suburbOptions={actualLocationProfiles}
          suburbSlugsArray={state.suburbs}
          sortBy={state.sortBy}
          agentType={state.agentFilter}
          keyword={state.keywordName}
          breadcrumbs={breadcrumbs}
          agencyIds={agencyIds}
          user={user}
          agentResults={data?.contactSearchBySuburbs?.results}
          agencyResults={data?.agencySearchBySuburbs?.results}
        />
        <div css={styles.breadcrumbBar}>
          <div css={[styles.wrapper, styles.breadcrumbs]}>
            <Breadcrumbs
              baseUrl={publicRuntimeConfig.BASE_URL}
              breadcrumbs={breadcrumbs}
              backButtonLabel="Back"
            />
          </div>
        </div>
        <SearchStickyBar
          isAgency={isAgency}
          currentAgentType={state.agentFilter}
          selectedSuburbsKey={selectedSuburbsKey}
          initialSelectedOption={initialSelectedOption}
          selectSuburbs={selectSuburbs}
          selectSearchType={selectSearchType}
          selectAgentType={selectAgentType}
          onOpenFilters={handleOpenFilters}
          onAgentTypeSearchSubmit={handleAgentTypeSearchSubmit}
        />
        <main
          id={skipLinkContentId}
          data-testid="trade-search-results-2026__page"
        >
          <div css={styles.wrapper}>
            <div css={styles.grid}>
              <div css={styles.headingSection}>
                <div css={styles.headingRow}>
                  <h1 css={styles.heading} aria-live="polite">
                    {formatResultsHeading({
                      total: search?.total,
                      isAgency,
                      locations: actualLocationProfiles,
                    })}
                  </h1>
                  <SortBySelect
                    isAgency={isAgency}
                    selectedSortBy={state.sortBy}
                    setSortBy={setSortBy}
                  />
                </div>
                <RmaDisclaimer />
              </div>
              <div
                css={styles.results}
                data-testid="search-results-2026__results"
              >
                <LoadingOverlay
                  isLoading={isLoading}
                  contentCss={styles.resultsContent}
                >
                  <SearchResultCards
                    isAgency={isAgency}
                    agentResults={data?.contactSearchBySuburbs?.results}
                    agencyResults={data?.agencySearchBySuburbs?.results}
                    suburbName={
                      actualLocationProfiles[0]?.suburbName ?? undefined
                    }
                    isPropertyManager={isPropertyManagerFilter(
                      state.agentFilter,
                    )}
                    page={page}
                    baseProperties={baseProperties}
                    onTapAgentCard={trackTapAgentCard}
                    onTapAgencyCard={trackTapAgencyCard}
                  />
                </LoadingOverlay>
              </div>
              <div
                css={[styles.pagination, isLoading && styles.dimmed]}
                data-testid="search-paginator"
                aria-hidden={isLoading || undefined}
              >
                <Paginator
                  totalPages={search?.totalPages ?? 0}
                  currentPage={page}
                  urlTemplate={buildPaginatorUrl(state)}
                  onPageChange={handlePageChange}
                />
              </div>
              <aside css={styles.rail}>
                <AppraisalCtaCard
                  baseUrl={publicRuntimeConfig.BASE_URL}
                  isAgency={isAgency}
                />
              </aside>
            </div>
          </div>
        </main>
        {isEmbeddedApp ? null : (
          <SearchResultsFooterWithFragment
            id={
              actualLocationProfiles.length === 1
                ? actualLocationProfiles[0]?.id
                : null
            }
          />
        )}
      </div>
    </MediaQueryProvider>
  );
};

export default SearchResults2026;
